import React, { useContext, useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import dateformat from "dateformat";

import styles from './styles.module.scss';
import { fetchStreamChildren, fetchStreams } from "src/redux/actions/streams";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Reveal from "src/components/animation";
import { fetchCollection, fetchCollections, fetchCollectionSection } from "src/redux/actions/collections";
import InfiniteScroll from "src/components/infiniteScroll";
import LinesEllipsis from "react-lines-ellipsis";
import { USER_DEFINED } from "src/constants/collections";
import { useSpring, animated } from "@react-spring/web";
import { Stream } from "src/types/streams";
import { Collection, CollectionSection } from "src/types/collections";
import Thumbnail from "src/components/thumbnail";
import { FileService } from "src/utils/file.service";
import useMeasure from "react-use-measure";
import { fetchGroupChildren } from "src/redux/actions/groups";
import { useLocation, useParams } from "react-router-dom";
import { validDocuments, validPresentations } from "src/constants/createAsset";
import { CreateAssetFileType } from "src/types/hub";
import { IPage } from "src/types/assets";
import { useStoreState } from "src/pages/contentMap";
import { fetchPreviewAsset, fetchPreviewTextLayer, removeAssetPage, selectAssetPage } from "src/redux/actions/assets";
import { PageEntityContext } from "../index";
import Text from "src/components/text";
import userRoleService from "src/utils/userRoles.service";

interface EntityBrowserProps {
  className?: string;
  width: number;
  entity?: Stream | Collection | CollectionSection;
  items?: any[];
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
  loading?: boolean;
}

const EntityBrowser: React.FC<EntityBrowserProps> = ({ loading, className, width, items, action, onSelection }) => {

  var items = items;

  const { fileType: SupportedFileType } = React.useContext(PageEntityContext);

  if (_.size(items) > 0) {
    // filtering to assets for create/update asset
    const supportedTypes = SupportedFileType === CreateAssetFileType.PDF ? validDocuments : validPresentations;

    var filteredItems = _.filter(items, (item) => {
      const entityType = _.get(item, "entityType");
      if (entityType === "asset") {
        const fileType = FileService.getFileType(_.get(item, "metadata"));
        if (!_.includes(supportedTypes, fileType)) {
          return false;
        }
        return true;
      }
      return true;
    })

    items = filteredItems;
  }

  return (
    <div className={cx(styles.container, className)}>
      {_.size(items) === 0 && !loading && <p className={styles.errorMessage}>No assets found</p>}

      {items && items.map((item) => (
        <EntityRow key={item.id} entity={item} width={width} action={action} onSelection={onSelection} />
      ))}
    </div>
  );
}


const StreamsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, streams } = useSelector((state: any) => {
    return {
      loading: state.hub.streams.loading,
      streams: state.hub.streams?.items,
    };
  }, _.isEqual);

  useEffect(() => {
    dispatch(fetchStreams());
  }, []);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        <StreamPlaceholder width={'50%'} />
        <StreamPlaceholder width={'70%'} />
        <StreamPlaceholder width={'60%'} />
        <StreamPlaceholder width={'50%'} />
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser className={className} items={streams} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}

const StreamBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.stream.items[entity!.id!]?.loadingChildren,
      items: state.stream.items[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchStreamChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser loading={loading} className={className} width={width} items={items} action={action} onSelection={onSelection} />
      </InfiniteScroll>
    </div>
  );
}

const GroupBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.group[entity!.id!]?.loadingChildren,
      items: state.group[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchGroupChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser loading={loading} className={className} width={width} items={items} action={action} onSelection={onSelection} />
      </InfiniteScroll>
    </div>
  );
}

const CollectionsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector((state: any) => {
    const collectionState = _.get(state, 'collections');
    const loading = _.get(collectionState, 'loading');
    const pagination = _.get(collectionState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const collections = (_.get(collectionState, 'items') || []).filter((item: any) => {
      return item.type === USER_DEFINED;
    });

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      collections: collections,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    if (userRoleService.canAccessCollection()) {
      fetchCollectionsForPage(0);
    }
  }, []);

  const fetchCollectionsForPage = (page: number) => {

    const request = {
      page: page,
      limit: 20,
      sort: {
        field: 'name',
        order: 'asc'
      }
    };

    dispatch(fetchCollections(request));
  }

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      fetchCollectionsForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          if (_.size(collections) === 0) {
            return (<>
              <CollectionPlaceholder width={'50%'} />
              <CollectionPlaceholder width={'70%'} />
              <CollectionPlaceholder width={'60%'} />
              <CollectionPlaceholder width={'50%'} />
            </>)
          } else {
            return <CollectionPlaceholder width={'70%'} />
          }
        }}
      >
        <EntityBrowser className={className}
          width={width}
          items={collections}
          action={action}
          onSelection={onSelection}
        />
      </InfiniteScroll>
    </div>
  );
}

const CollectionBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const collection = entity as Collection;
  const childrenType = collection.sectionsCount! > 0 ? 'collection_section' : 'asset';

  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    const collection = state.collections.collection[entity!.id!]?.item;
    let items = [];
    if (collection) {
      if (_.size(collection.sections) > 0) {
        items = (collection.sections || []).map((item: any) => {
          return {
            ...item.item,
            entityType: 'collection_section'
          }
        });
      } else if (_.size(collection.assets) > 0) {
        items = (collection.assets || []).map((item: any) => {
          return {
            ...item.asset
          }
        });
      }
    }

    return {
      loading: state.collections.collection[entity!.id!]?.loading,
      items: items,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchCollection(entity.id));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        {childrenType === 'collection_section' && (
          <>
            <CollectionSectionPlaceholder width="50%" />
            <CollectionSectionPlaceholder width="70%" />
            <CollectionSectionPlaceholder width="60%" />
            <CollectionSectionPlaceholder width="50%" />
          </>
        )}

        {childrenType === 'asset' && (
          <>
            {_.times((collection.assetsCount || 3), (index) => (
              <AssetPlaceholder key={index} />
            ))}
          </>
        )}

      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser loading={loading} className={className} items={items} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}

const CollectionSectionBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const collectionSection = entity as CollectionSection;

  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    const collectionState = state.collections.collection[collectionSection!.collection!]?.item;
    if (!collectionState) {
      return {
        loading: false,
        items: []
      }
    }

    let items = [];
    let loading = false;
    const sectionState = collectionState.sections.find(section => section.item?.id === collectionSection.id);

    if (sectionState) {
      items = (sectionState.item.assets || []).map((item: any) => item.asset);
      loading = sectionState.loading;
    }

    return {
      loading: loading,
      items: items,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchCollectionSection({
        collection: collectionSection!.collection!,
        section: entity.id
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        <>
          {_.times(3, (index) => (
            <AssetPlaceholder key={index} />
          ))}
        </>
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser loading={loading} className={className} items={items} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}

/**
 * Entity Renders
*/

interface EntityRowProps {
  entity: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const Component = useMemo(() => {
    const type = entity?.entityType;
    switch (type) {
      case 'stream':
        return StreamRow;
      case 'group':
        return GroupRow;
      case 'collection':
        return CollectionRow;
      case 'collection_section':
        return CollectionSectionRow;
      case 'asset':
        return AssetRow;
      default:
        return AssetRow;
    }
  }, [entity]);


  if (!Component) {
    return null;
  }

  return (
    React.createElement(Component, { entity, width, action, onSelection })
  );
}

const StreamRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, { [styles.expanded]: expanded })} style={{ width: width }} onClick={handleClick}>
        <div className={styles.name}>
          <Text text={entity?.name} lines={1} tooltip={{placement: "auto"}} />
        </div>
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown", {
              [styles.invertedDropdown]: expanded
            })}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <StreamBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const GroupRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, { [styles.expanded]: expanded })} style={{ width: width }} onClick={handleClick}>
        <div className={styles.name}>
          <Text text={entity?.name} lines={1} tooltip={{placement: "auto"}} />
        </div>
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown", {
              [styles.invertedDropdown]: expanded
            })}></i>
          </button>
        </div>
      </div>
      <animated.div style={childrenAnimation}>
        {mountChildren && (
          <animated.div style={childrenAnimation} className={styles.children}>
            <div ref={containerRef} className={styles.inner}>
              <GroupBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
            </div>
          </animated.div>
        )}
      </animated.div>
    </>
  );
}

const CollectionRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection)} style={{ width: width }} onClick={handleClick}>
        <div className={styles.inner}>
          <Text className={styles.name} text={entity?.name} lines={1} tooltip={{placement: "auto"}} />
        </div>
        <div className={styles.actions}>
          <span className={styles.count}>
            {entity?.sectionsCount > 0 ? `${entity?.sectionsCount} Sections` : ''} 
          </span>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown", {
              [styles.invertedDropdown]: expanded
            })}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <CollectionBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const CollectionSectionRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection)} style={{ width: width }} onClick={handleClick}>
        <Text className={styles.name} text={entity?.name} lines={1} tooltip={{placement: "auto"}} />
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown", {
              [styles.invertedDropdown]: expanded
            })}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <CollectionSectionBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const AssetRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {


  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();
  const selectedFileType = useContext(PageEntityContext).fileType;

  const fileType = useMemo(() => {
    return FileService.getFileType(entity.metadata);
  }, [entity]);

  const updatedDate = _.get(entity, 'contentModifiedDate');
  const pageCount = _.get(entity, 'metadata.pages');
  const dispatch = useDispatch();
  const assetsPreview = useSelector((state: any) => _.get(state, "asset.assetsPreview"));

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);


  const handleClick = () => {
    handleSelection(entity);
    setExpanded(!expanded);
    setMountChildren(true);
  }

  const handleSelection = (entity: any) => {

    const assetPreview = _.get(assetsPreview, _.get(entity, "id", ""), {});
    if (assetPreview && assetPreview.succeeded) {
      return;
    }

    const assetId = _.get(entity, "id", "");
    
    dispatch(fetchPreviewTextLayer(assetId));
    dispatch(fetchPreviewAsset({id: assetId}));

  }

  return (
    <>
      <div className={cx(styles.entity, styles.asset)} style={{ width: width }} onClick={handleClick}>
        <div className={styles.inner}>
          <Thumbnail className={cx(styles.icon, styles[selectedFileType.toLowerCase()])} media={entity?.icon}  defaultImage="/images/assets/default_asset.jpg" />
          <div className={styles.details}>
            <div className={styles.innerContainer}>
              <div >
                <Text className={styles.name} text={entity?.name} lines={1} tooltip={{placement: "auto"}} />
              </div>
              <div className={styles.meta}>
                {fileType} {pageCount && `| ${pageCount} Pages`}
              </div>
            </div>
            <div className={styles.date}>
              {updatedDate && `Updated ${dateformat(updatedDate, 'dd mmm yyyy')}`}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown", {
              [styles.invertedDropdown]: expanded
            })}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={cx(styles.inner, styles.assetPagesContainer)}>
            <RenderPages entity={entity} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const RenderPages: React.FC<any> = ({ entity }) => {

  const dispatch = useDispatch();
  const selectedPages = useSelector((state) => _.get(state, "asset.selectedPages"), _.isEqual);
  const assetPages = useSelector((state) => _.get(state, `asset.assetsPreview[${entity.id}]`), _.isEqual) as any;
  const { loading, succeeded, item, pagesWithTexts } = assetPages || {};
  const { query, fileType } = React.useContext(PageEntityContext);
  let highlightedPages = []
  if (_.size(pagesWithTexts?.pages) > 0 && _.size(query) > 0) {

    highlightedPages = pagesWithTexts.pages.filter(page => {
      let allTexts = page.texts.reduce((concatenatedText, text, index) => {
        if (index <= page.texts.length - 1) {
          return concatenatedText + page.texts[index].t + " ";
        } else {
          return concatenatedText;
        }
      }, '');

      allTexts = allTexts.toLowerCase();

      if (new RegExp(`\\b${query}\\b`).test(allTexts)) {
        return page;
      }
    }
    )
  };

  const highlightedPagesIds = _.map(highlightedPages, (page) => _.get(page, "background.id"));

  if (loading) {
    return (
      <div className={styles.pages}>
        <div className={styles.pagePlaceholderContainer}>
          <PagePlaceholder />
          <PagePlaceholder />
          <PagePlaceholder />
        </div>
      </div>
    )
  }

  if (!loading && succeeded && !item) {
    return <div className={styles.errorMessage}>No Pages Found</div>
  }

  const handlePageClick = (page: IPage) => {
    const pageId = _.get(page, "background.id");

    if (_.find(selectedPages, (item) => _.get(item, "background.id") === _.get(page, "background.id"))) {
      dispatch(removeAssetPage({ ...page, asset: entity.id , assetName : entity.name }));
    }
    else {
      dispatch(selectAssetPage({ ...page, asset: entity.id , assetName : entity.name }));
      requestAnimationFrame(() => {
        const el = document.getElementById(`page_${pageId}`);
        el?.scrollIntoView({ behavior: "smooth" });
      });
    }
  };


  return (
    <Reveal show={item}>
      <div className={cx(styles.assetPage, styles[fileType.toLowerCase()])}>
        {_.size(item.pages) > 0 && item.pages.map((page: any, index: number) => {
          const isPageSelected = _.find(selectedPages, (item) => _.get(item, "background.id") === _.get(page, "background.id"));
          const highlightPage = _.includes(highlightedPagesIds, _.get(page, "background.id"));
          return (
            <div key={_.get(page, "background.id")} className={styles.page} onClick={() => handlePageClick(page)}>
              <p className={styles.index}>{index + 1 < 10 && 0}{index + 1}</p>
              {highlightPage && <div className={styles.dot} />}

              <div className={styles.innerContainer}>
                <i className={cx(isPageSelected ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected', {
                  [styles.active]: isPageSelected
                })} />
                <img className={cx(styles[fileType.toLowerCase()])} src={_.get(page, "background.full")} />
                <div className={cx(styles.overlay, styles[fileType.toLowerCase()], { [styles.activeOverlay]: isPageSelected})} />
              </div>
            </div>
          )
        })
        }
      </div>
    </Reveal>
  )
}

export const StreamPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}

export const CollectionPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}

export const CollectionSectionPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}

export const AssetPlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.entity, styles.asset, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.meta}>
            <span className={styles.block} />
          </div>
        </div>
      </div>
    </div>
  );
}

export const PagePlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.pagePlaceholder, styles.placeholder)}>
      <div className={cx(styles.img, styles.block)} />
    </div>
  );
}

export { EntityBrowser, StreamsBrowser, CollectionsBrowser };